import { queryClient, userManager } from '@/components/authentication/keycloak.js';
import {
  finishSessionLogout,
  SESSION_STATUS,
  setSessionRefreshUnavailable,
  startSessionLogout,
} from '@/store/slices/authSlice.js';
import store from '@/store/store.js';

export const SESSION_OPERATION_TIMEOUT_MS = 15_000;

export const SESSION_LOGOUT_REASONS = {
  MANUAL: 'manual',
  TOKEN_UNAVAILABLE: 'token_unavailable',
  AUTHENTICATION_REJECTED: 'authentication_rejected',
  OIDC_ERROR: 'oidc_error',
  REFRESH_UNAVAILABLE: 'refresh_unavailable',
};

export class SessionError extends Error {
  constructor(reason, cause) {
    super('The authenticated session is unavailable.');
    this.name = 'SessionError';
    this.reason = reason;
    if (cause !== undefined) this.cause = cause;
  }
}

const noop = async () => {};
let clearApolloStore = noop;
let logoutPromise = null;
let refreshPromise = null;
let oidcEventCleanup = null;
let sessionChannel = null;
let sessionChannelName = null;

const withTimeout = (promise, operation) => new Promise((resolve, reject) => {
  const timeoutId = setTimeout(() => {
    reject(new Error(`${operation} exceeded the ${SESSION_OPERATION_TIMEOUT_MS}ms timeout`));
  }, SESSION_OPERATION_TIMEOUT_MS);

  Promise.resolve(promise).then(
    (value) => {
      clearTimeout(timeoutId);
      resolve(value);
    },
    (error) => {
      clearTimeout(timeoutId);
      reject(error);
    }
  );
});

const hasAccessToken = (user) =>
  typeof user?.access_token === 'string' && user.access_token.trim().length > 0;

const hasUnexpiredAccessToken = (user) => {
  const expiresAt = Number(user?.expires_at);
  return hasAccessToken(user) && Number.isFinite(expiresAt) && expiresAt > Date.now() / 1000 && !user.expired;
};

const getSessionChannelName = (user) => {
  const sessionId = user?.profile?.sid || user?.session_state;
  const authority = userManager.settings?.authority;
  const clientId = userManager.settings?.client_id;
  if (!sessionId || !authority || !clientId || typeof window === 'undefined') return null;
  return [window.location.origin, window.ENV?.BACKEND_URL || '', authority, clientId, sessionId]
    .map((value) => encodeURIComponent(value))
    .join('|');
};

const configureSessionChannel = (user) => {
  const channelName = getSessionChannelName(user);
  if (channelName === sessionChannelName) return;

  sessionChannel?.close();
  sessionChannel = null;
  sessionChannelName = null;
  if (!channelName || typeof BroadcastChannel !== 'function') return;

  try {
    sessionChannel = new BroadcastChannel(`kubrik-session-logout:${channelName}`);
    sessionChannelName = channelName;
    sessionChannel.addEventListener('message', (event) => {
      if (sessionChannelName === channelName && event.data?.type === 'session-logout' && isSessionActive()) {
        void logout({
          reason: SESSION_LOGOUT_REASONS.MANUAL,
          broadcast: false,
          redirectToProvider: false,
        });
      }
    });
  } catch (error) {
    console.warn('[Session] Cross-tab notifications are unavailable.', error);
  }
};

const broadcastSessionLogout = (user) => {
  const channelName = user === undefined ? sessionChannelName : getSessionChannelName(user);
  if (!channelName || typeof BroadcastChannel !== 'function') return false;
  if (channelName !== sessionChannelName) configureSessionChannel(user);
  if (!sessionChannel) return false;
  try {
    sessionChannel.postMessage({ type: 'session-logout' });
    return true;
  } catch (error) {
    console.warn('[Session] Failed to notify other tabs.', error);
    return false;
  }
};

export const isTransientRefreshError = (error) => {
  if (error?.reason === SESSION_LOGOUT_REASONS.REFRESH_UNAVAILABLE) return true;
  if (error?.innerError) return isTransientRefreshError(error.innerError);
  const code = String(error?.error ?? error?.code ?? '').toLowerCase();
  if (['invalid_grant', 'login_required', 'interaction_required', 'access_denied', 'invalid_token', 'invalid_client'].includes(code)) {
    return false;
  }
  if (['server_error', 'temporarily_unavailable'].includes(code)) return true;
  const status = Number(error?.status ?? error?.statusCode ?? error?.response?.status);
  if (status === 408 || status === 429 || status >= 500) return true;
  if (status >= 400 && status < 500) return false;

  return error instanceof TypeError ||
    ['AbortError', 'TimeoutError', 'ErrorTimeout'].includes(error?.name) ||
    /network|fetch|timeout|timed out|offline|temporar/i.test(String(error?.message ?? ''));
};

const registerOidcEvents = () => {
  const events = userManager.events;
  const removers = [];
  const bind = (addMethod, removeMethod, handler) => {
    if (typeof events?.[addMethod] !== 'function') return;
    events[addMethod](handler);
    if (typeof events[removeMethod] === 'function') {
      removers.push(() => events[removeMethod](handler));
    }
  };
  const renew = () => {
    void getValidAccessToken(true).catch(() => {});
  };
  const userLoaded = (user) => configureSessionChannel(user);

  bind('addUserLoaded', 'removeUserLoaded', userLoaded);
  bind('addAccessTokenExpiring', 'removeAccessTokenExpiring', renew);
  bind('addAccessTokenExpired', 'removeAccessTokenExpired', renew);
  bind('addSilentRenewError', 'removeSilentRenewError', renew);
  bind('addUserSignedOut', 'removeUserSignedOut', () => {
    void logout({ reason: SESSION_LOGOUT_REASONS.OIDC_ERROR });
  });

  return () => removers.reverse().forEach((remove) => remove());
};

export const initializeSession = ({ clearApolloStore: clearStore } = {}) => {
  if (typeof clearStore === 'function') {
    clearApolloStore = clearStore;
  }
  if (!oidcEventCleanup) {
    const removeListeners = registerOidcEvents();
    const cleanup = () => {
      if (oidcEventCleanup !== cleanup) return;
      removeListeners();
      oidcEventCleanup = null;
      clearApolloStore = noop;
      sessionChannel?.close();
      sessionChannel = null;
      sessionChannelName = null;
    };
    oidcEventCleanup = cleanup;
  }
  return oidcEventCleanup;
};

export const isSessionActive = () =>
  store.getState?.()?.auth?.sessionStatus === SESSION_STATUS.ACTIVE;

const runCleanup = async (label, cleanup) => {
  try {
    await withTimeout(Promise.resolve().then(cleanup), label);
  } catch (error) {
    console.warn(`[Session] Failed to ${label}.`, error);
  }
};

const failSession = (reason, cause) => {
  void logout({ reason });
  throw new SessionError(reason, cause);
};

const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  let timedOut = false;
  const refreshRequest = (async () => {
    let lastError;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        if (!isSessionActive()) throw new SessionError(SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE);
        if (timedOut) throw new SessionError(SESSION_LOGOUT_REASONS.REFRESH_UNAVAILABLE, lastError);
        const refreshedUser = await userManager.signinSilent({ silentRequestTimeoutInSeconds: 5 });
        if (!isSessionActive()) {
          await runCleanup('discard a late OIDC refresh', () => userManager.removeUser());
          throw new SessionError(SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE);
        }
        if (!hasUnexpiredAccessToken(refreshedUser)) {
          throw new SessionError(SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE);
        }
        configureSessionChannel(refreshedUser);
        store.dispatch(setSessionRefreshUnavailable(false));
        return refreshedUser.access_token;
      } catch (error) {
        if (error instanceof SessionError) throw error;
        if (!isTransientRefreshError(error)) {
          throw new SessionError(SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE, error);
        }
        lastError = error;
      }
    }
    throw new SessionError(SESSION_LOGOUT_REASONS.REFRESH_UNAVAILABLE, lastError);
  })();

  const boundedRefresh = withTimeout(refreshRequest, 'Refreshing the access token')
    .catch((error) => {
      if (error instanceof SessionError) {
        if (error.reason === SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE && isSessionActive()) {
          void logout({ reason: SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE });
        }
        if (error.reason === SESSION_LOGOUT_REASONS.REFRESH_UNAVAILABLE) {
          store.dispatch(setSessionRefreshUnavailable(true));
        }
        throw error;
      }
      if (isTransientRefreshError(error)) {
        timedOut = true;
        store.dispatch(setSessionRefreshUnavailable(true));
        throw new SessionError(SESSION_LOGOUT_REASONS.REFRESH_UNAVAILABLE, error);
      }
      return failSession(SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE, error);
    });
  refreshPromise = boundedRefresh;
  // Le délai local ne doit pas autoriser une deuxième rotation tant que la requête OIDC n'est pas terminée.
  const releaseRefresh = () => {
    if (refreshPromise === boundedRefresh) refreshPromise = null;
  };
  void refreshRequest.then(releaseRefresh, releaseRefresh);

  return refreshPromise;
};

export const getValidAccessToken = async (forceRefresh = false) => {
  if (!isSessionActive()) {
    throw new SessionError(SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE);
  }
  if (refreshPromise) return refreshPromise;
  if (forceRefresh) return refreshAccessToken();

  try {
    const user = await withTimeout(
      Promise.resolve().then(() => userManager.getUser()),
      'Reading the OIDC session'
    );
    if (!isSessionActive()) {
      throw new SessionError(SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE);
    }
    configureSessionChannel(user);
    if (refreshPromise) return refreshPromise;
    if (!user || !hasAccessToken(user)) {
      return failSession(SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE);
    }

    const expiresAt = Number(user.expires_at);
    const secondsUntilExpiry = expiresAt - Date.now() / 1000;
    if (!forceRefresh && hasUnexpiredAccessToken(user) && secondsUntilExpiry >= 60) {
      return user.access_token;
    }

    try {
      return await refreshAccessToken();
    } catch (error) {
      if (!isSessionActive()) throw new SessionError(SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE, error);
      if (isTransientRefreshError(error) && hasUnexpiredAccessToken(user)) return user.access_token;
      throw error;
    }
  } catch (error) {
    if (error instanceof SessionError) throw error;
    return failSession(SESSION_LOGOUT_REASONS.TOKEN_UNAVAILABLE, error);
  }
};

export const logout = ({
  reason = SESSION_LOGOUT_REASONS.MANUAL,
  broadcast = true,
  redirectToProvider = true,
} = {}) => {
  if (logoutPromise) return logoutPromise;

  logoutPromise = Promise.resolve().then(async () => {
    let user = null;
    const hasBroadcasted = broadcast && broadcastSessionLogout();
    await Promise.all([
      runCleanup('remove the local OIDC user', async () => {
        try {
          user = await withTimeout(userManager.getUser(), 'Reading the logout hint');
          configureSessionChannel(user);
          if (broadcast && !hasBroadcasted) broadcastSessionLogout(user);
        } finally {
          await userManager.removeUser();
        }
      }),
      runCleanup('clear the Apollo store', () => clearApolloStore()),
      runCleanup('clear the React Query cache', () => queryClient.clear()),
    ]);

    store.dispatch(finishSessionLogout());

    if (redirectToProvider) {
      const signoutArguments = {
        post_logout_redirect_uri: window.location.origin,
      };
      if (user?.id_token) {
        signoutArguments.id_token_hint = user.id_token;
      }

      await runCleanup('redirect to the identity provider', () =>
        userManager.signoutRedirect(signoutArguments)
      );
    }
  });

  store.dispatch(startSessionLogout(reason));
  if (!redirectToProvider) {
    logoutPromise = logoutPromise.finally(() => {
      logoutPromise = null;
    });
  }
  return logoutPromise;
};